import { useMutation } from '@apollo/client'
import { Formik } from 'formik'
import { ReactElement } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'

import { Input } from 'components/Input'
import { Button } from 'components/v2/Button'
import { Column } from 'components/v2/Column'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import Heading from 'components/v2/Heading'
import Modal from 'components/v2/Modal'
import { Row } from 'components/v2/Row'
import { REGISTER_BOOKING_TAX_INVOICED } from 'features/Bookings/mutations-legacy'
import { IRegisterBookingTaxInvoicedValues } from 'features/Bookings/types'
import { GTBookingInvoicedBy, GTBookingV2 } from 'gql/types.generated'
import { getUnprocessableErrors } from 'utils/error'
import { schema } from './schema'

const messages = defineMessages({
  taxFolioPlaceholder: {
    id: 'bookings.registerBookingTaxInvoiced.taxFolio.placeholder',
    defaultMessage: 'Enter folio'
  }
})

interface IRegisterBookingTaxInvoicedModalProps {
  booking: GTBookingV2
  closeModal: () => void
}

export const RegisterBookingTaxInvoicedModal = ({
  booking,
  closeModal
}: IRegisterBookingTaxInvoicedModalProps): ReactElement => {
  const intl = useIntl()

  const [registerBookingTaxInvoiced, { loading, error }] = useMutation(REGISTER_BOOKING_TAX_INVOICED, {
    onError: (): void => {
      // Do nothing
    },
    update(cache) {
      cache.modify({
        id: cache.identify(booking),
        fields: {
          isTaxInvoiced() {
            return true
          },
          invoicedBy() {
            return GTBookingInvoicedBy.Finance
          }
        }
      })
      closeModal()
    }
  })

  const handleSubmit = (values: IRegisterBookingTaxInvoicedValues): void => {
    registerBookingTaxInvoiced({
      variables: {
        input: {
          bookingID: booking.id,
          taxFolio: values.taxFolio
        }
      }
    })
  }

  const initialValues: IRegisterBookingTaxInvoicedValues = {
    taxFolio: ''
  }

  return (
    <Modal.Container>
      <Modal.Header>
        <Heading.H5>
          <FormattedMessage id="bookings.registerBookingTaxInvoiced.title" defaultMessage="Finance bill" />
        </Heading.H5>
      </Modal.Header>
      <Formik initialValues={initialValues} validationSchema={schema(intl)} onSubmit={handleSubmit}>
        {(props): ReactElement => {
          const { values, handleChange, handleBlur, errors, touched } = props

          return (
            <form onSubmit={props.handleSubmit}>
              <Modal.Content>
                <Row>
                  <Column>
                    <Input
                      id="taxFolio"
                      value={values.taxFolio}
                      placeholder={intl.formatMessage(messages.taxFolioPlaceholder)}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      errors={errors.taxFolio}
                      touched={touched.taxFolio}
                      label={
                        <FormattedMessage
                          id="bookings.registerBookingTaxInvoiced.taxFolio.label"
                          defaultMessage="Folio"
                        />
                      }
                    />
                  </Column>
                </Row>
              </Modal.Content>
              <Modal.Footer>
                {error && (
                  <Modal.ErrorMessage>
                    <ErrorMessage errors={getUnprocessableErrors(error)}>
                      <FormattedMessage
                        id="bookings.registerBookingTaxInvoiced.error"
                        defaultMessage="There was an error while registering the tax invoiced"
                      />
                    </ErrorMessage>
                  </Modal.ErrorMessage>
                )}
                <Modal.Actions>
                  <Button kind="cancel" onClick={(): void => closeModal()}>
                    <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
                  </Button>
                  <Button type="submit" kind="primary" loading={loading}>
                    <FormattedMessage
                      id="bookings.registerBookingTaxInvoiced.submit"
                      defaultMessage="Mark as invoiced"
                    />
                  </Button>
                </Modal.Actions>
              </Modal.Footer>
            </form>
          )
        }}
      </Formik>
    </Modal.Container>
  )
}
